import { useState, useCallback, useRef, useEffect } from "react"
import FormularioDatosDelCliente from "../scoring/formularios/datos-del-cliente"
import FormularioHistorialyRiesgo from "../scoring/formularios/historal-riesgo"
import FormularioInversionYDistribuidor from "../scoring/formularios/inversion-y-distribuidor"
import { FormularioDTO } from "app/analista/scoring/adapters/dto/formulario-dto"
import formularioNuevo from "app/analista/scoring/adapters/ports/formulario_api"

const fieldToResponseMap = {
  "Datos del cliente": {
    date_of_incorporation: "months_of_incorporation",
    preapproved_incomes: "incomes_score",
    incomes_extracts: "incomes_score",
    transunion_amounts: "amounts_score",
    department: "department_score",
  },
  "Historial y Riesgo": {
    score_credit_centers: "centers_score",
    truora_score: "score_truora",
    arrears_amount: "score_arrears",
    arrears_oldness: "score_arrears",
    liabilities: "score_arrears",
    seized_accounts: "accounts_seized_denied",
  },
  "Inversión y Distribuidor": {
    value: "finance_percentage_score",
    financial_percent: "financial_percent",
    deadline: "deadline",
    indispensable_asset: "indispensable_asset_score",
    invested_in_deal: "invested_score",
    annual_sales_distributor: "distributor_sells_score",
    credit_score_distributor: "distributor_portfolio_behaviour_score",
    coverage: "coverage_score",
  },
}

const requiredFields = {
  "Datos del cliente": [
    "date_of_incorporation",
    "preapproved_incomes",
    "incomes_extracts",
    "transunion_amounts",
    "department",
  ],
  "Historial y Riesgo": [
    "score_credit_centers",
    "truora_score",
    "arrears_amount",
    "arrears_oldness",
    "liabilities",
    "seized_accounts",
  ],
  "Inversión y Distribuidor": [
    "value",
    "financial_percent",
    "deadline",
    "indispensable_asset",
    "invested_in_deal",
    "annual_sales_distributor",
    "credit_score_distributor",
    "coverage",
  ],
}

const steps = [
  { label: "Datos del cliente", component: FormularioDatosDelCliente },
  { label: "Historial y Riesgo", component: FormularioHistorialyRiesgo },
  { label: "Inversión y Distribuidor", component: FormularioInversionYDistribuidor },
]

interface StepperScoringProps {
  formulario: any
  setFormulario: any
  editable: boolean
  onCompletionChange?: (isComplete: boolean) => void
  onCalculate?: () => void
  isCalculating?: boolean
}

interface ApiResponse {
  approved: boolean
  score: number
  resume: Record<string, number | boolean | string>
}

export default function StepperScoring({
  formulario,
  setFormulario,
  editable,
  onCompletionChange,
  onCalculate,
  isCalculating = false,
}: StepperScoringProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const previousStepRef = useRef(currentStep)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([false, false, false])
  const [visitedSteps, setVisitedSteps] = useState<boolean[]>([true, false, false])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isMounted = useRef(true)
  const isLastStep = currentStep === steps.length - 1

  const countFieldsWithValue = useCallback(
    (stepIndex: number, targetValue: number): number => {
      if (!formulario.resume) return 0

      const stepName = steps[stepIndex].label
      const stepMapping = fieldToResponseMap[stepName as keyof typeof fieldToResponseMap]

      const responseFields = Object.values(stepMapping)

      let count = 0
      Object.entries(formulario.resume).forEach(([key, value]) => {
        if (responseFields.includes(key) && typeof value === "number" && value === targetValue) {
          count++
        }
      })

      return count
    },
    [formulario.resume],
  )

  const checkStepCompletion = useCallback(
    (stepIndex: number) => {
      const stepName = steps[stepIndex].label
      const fields = requiredFields[stepName as keyof typeof requiredFields]

      return fields.every((field) => {
        const value = formulario[field]
        if (typeof value === "boolean") {
          return true
        }
        return value !== undefined && value !== null && value !== ""
      })
    },
    [formulario],
  )

  const hasAnyFieldFilled = useCallback(
    (stepIndex: number) => {
      const stepName = steps[stepIndex].label
      const fields = requiredFields[stepName as keyof typeof requiredFields]

      return fields.some((field) => {
        const value = formulario[field]
        if (typeof value === "boolean") {
          return true
        }
        return value !== undefined && value !== null && value !== ""
      })
    },
    [formulario],
  )

  useEffect(() => {
    const newCompletedSteps = steps.map((_, index) => checkStepCompletion(index))
    setCompletedSteps(newCompletedSteps)

    if (onCompletionChange) {
      const allComplete = newCompletedSteps.every((complete) => complete)
      onCompletionChange(allComplete)
    }
  }, [formulario, checkStepCompletion, onCompletionChange])

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (editable && !isCalculating) {
      setIsSubmitting(false)
    }
  }, [editable, isCalculating])

  const handleStepChange = useCallback(
    (stepIndex: number) => {
      if (stepIndex === currentStep || isTransitioning) return

      setIsTransitioning(true)
      previousStepRef.current = currentStep

      const newVisitedSteps = [...visitedSteps]
      newVisitedSteps[stepIndex] = true
      setVisitedSteps(newVisitedSteps)

      setTimeout(() => {
        if (isMounted.current) {
          setCurrentStep(stepIndex)
          setIsTransitioning(false)
        }
      }, 50)
    },
    [currentStep, isTransitioning, visitedSteps],
  )

  const handleCalculateSubmit = useCallback(async () => {
    if (!completedSteps.every((complete) => complete)) return
    setIsSubmitting(true)
    const formularioData = new FormularioDTO()
    const fieldsToCopy = [
      "type_person",
      "department",
      "invested_in_deal",
      "financial_percent",
      "indispensable_asset",
      "arrears_amount",
      "arrears_oldness",
      "liabilities",
      "score_credit_centers",
      "date_of_incorporation",
      "transunion_amounts",
      "preapproved_incomes",
      "incomes_extracts",
      "coverage",
      "truora_score",
      "seized_accounts",
      "credit_score_distributor",
      "annual_sales_distributor",
      "value",
      "deadline",
    ]

    fieldsToCopy.forEach((field) => {
      if (formulario[field] !== undefined) {
        ;(formularioData as any)[field] = formulario[field]
      }
    })

    try {
      const compiledData = FormularioDTO.compilarDTO(formularioData)
      const response = (await formularioNuevo.enviar(compiledData)) as ApiResponse

      const responseData = response
      setFormulario((prevFormulario: any) => ({
        ...prevFormulario,
        approved: responseData.approved,
        score: responseData.score,
        resume: responseData.resume,
        scoringResult: responseData,
      }))

      if (onCalculate) {
        onCalculate()
      }
      window.scrollTo({ top: 0, behavior: "smooth" })
    } catch (error) {
      console.error("Error al calcular el puntaje:", error)
    } finally {
      setIsSubmitting(false)
    }
  }, [completedSteps, formulario, setFormulario, onCalculate])

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      const newVisitedSteps = [...visitedSteps]
      newVisitedSteps[currentStep + 1] = true
      setVisitedSteps(newVisitedSteps)

      handleStepChange(currentStep + 1)
    } else if (isLastStep) {
      handleCalculateSubmit()
    }
  }, [currentStep, handleStepChange, visitedSteps, isLastStep, handleCalculateSubmit])

  const renderActiveComponent = () => {
    const ActiveComponent = steps[currentStep].component
    if (!ActiveComponent) {
      return <div>No component found for this step</div>
    }
    return <ActiveComponent formulario={formulario} setFormulario={setFormulario} editable={editable} />
  }

  const renderStepIcon = (index: number) => {
    if (formulario.resume) {
      const zeroCount = countFieldsWithValue(index, 0)
      if (zeroCount > 0) {
        return (
          <div className="bg-[#FED7D7] w-5 h-5 rounded-[2px] flex items-center justify-center">
            <span className="text-[#822727] text-xs font-medium">{zeroCount}</span>
          </div>
        )
      }

      const mediumCount = countFieldsWithValue(index, 2.5)
      if (mediumCount > 0) {
        return (
          <div className="bg-[#FEEBC8] w-5 h-5 rounded-[2px] flex items-center justify-center">
            <span className="text-[#7B341E] text-xs font-medium">{mediumCount}</span>
          </div>
        )
      }

      return null
    }

    if (completedSteps[index]) {
      return (
        <div className="bg-[#C6F6D5] w-5 h-5 rounded-[2px] flex items-center justify-center">
          <svg className="text-[#25855A] w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )
    }

    if (visitedSteps[index] && !completedSteps[index] && index !== currentStep && hasAnyFieldFilled(index)) {
      return (
        <div className="bg-[#FEEBCB] w-5 h-5 rounded-[2px] flex items-center justify-center">
          <svg className="text-[#C05621] w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.9998 8.99999V13M11.9998 17H12.0098M10.6151 3.89171L2.39019 18.0983C1.93398 18.8863 1.70588 19.2803 1.73959 19.6037C1.769 19.8857 1.91677 20.142 2.14613 20.3088C2.40908 20.5 2.86435 20.5 3.77487 20.5H20.2246C21.1352 20.5 21.5904 20.5 21.8534 20.3088C22.0827 20.142 22.2305 19.8857 22.2599 19.6037C22.2936 19.2803 22.0655 18.8863 21.6093 18.0983L13.3844 3.89171C12.9299 3.10654 12.7026 2.71396 12.4061 2.58211C12.1474 2.4671 11.8521 2.4671 11.5935 2.58211C11.2969 2.71396 11.0696 3.10655 10.6151 3.89171Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )
    }
    return null
  }

  return (
    <div className="w-full h-full flex flex-col mt-4">
      <div className="flex grid grid-cols-3 text-center gap-8 border-b border-gray-300 py-1.5">
        {steps.map((step, index) => (
          <div key={index} className="relative cursor-pointer" onClick={() => handleStepChange(index)}>
            <div className="flex items-center justify-center gap-2">
              <span className={`text-md font-semibold ${currentStep === index ? "text-[#390094]" : "text-[#4A5568]"}`}>
                {step.label}
              </span>
              {renderStepIcon(index)}
            </div>
            {currentStep === index && (
              <div className="absolute mt-2 left-0 w-full h-1 bg-[#41BFFB] rounded-full mt-1"></div>
            )}
          </div>
        ))}
      </div>

      <div
        className={`mt-8 flex-1 overflow-y-auto bg-white transition-opacity duration-150 ${
          isTransitioning ? "opacity-50" : "opacity-100"
        }`}
        key={currentStep}
      >
        {renderActiveComponent()}
      </div>

      {editable && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleNext}
            disabled={
              (isLastStep ? !completedSteps.every(Boolean) : !completedSteps[currentStep]) ||
              isSubmitting ||
              isCalculating
            }
            className={`w-[233px] h-[40px] mb-4 rounded-[28px] text-md font-semibold text-center py-2 transition-colors duration-200 flex items-center justify-center ${
              (isLastStep ? completedSteps.every(Boolean) : completedSteps[currentStep]) && !isSubmitting && !isCalculating
                ? "bg-[#390094] text-white cursor-pointer hover:bg-[#2d0075]"
                : "bg-[#CBD5E0] text-[#718096] cursor-not-allowed"
            }`}
          >
            {isLastStep ? (
              isSubmitting || isCalculating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Calculando
                </>
              ) : (
                "Calcular"
              )
            ) : (
              "Siguiente"
            )}
          </button>
        </div>
      )}
    </div>
  )
}
